/* eslint-disable no-nested-ternary */
import { useState } from 'react';
import styled from 'styled-components';
import { differenceInDays, parseISO } from 'date-fns';
import {
  Text2,
  Text3,
  Text4,
  Text4B,
  Text5,
  Text5B,
} from '../genericComponents/texts';
import infoImg from '../assets/img/info.svg';
import { BlankSpace } from '../genericComponents/layouts';
import Publications from '../models/publications.model';
import { priceFormat, roundPrice } from '../priceFormat';
import chileautosLogo from '../assets/img/chileautos.svg';
import RankingChileautos from '../models/rankingChileautos.model';
import { b2bBaseUrl } from '../config/config';
import bellImg from '../assets/img/bell.svg';

interface Props {
  publications: Publications;
  rankingChileautos:RankingChileautos;
  createdAt:string | undefined;
  haveSimilarVersions: boolean;
  haveYearPrices: boolean;
}

export default function PublicationsComponent({
  publications,
  rankingChileautos,
  createdAt,
  haveSimilarVersions,
  haveYearPrices,
}: Readonly<Props>) {
  const averagePublications = publications.price_average ?? 0;
  const countPublications = publications.list?.length ?? 0;
  const [activeTab, setActiveTab] = useState('Chileautos');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const tabs = ['Chileautos', 'Facebook', 'Mercado Libre', 'Yapo', 'Otras publicaciones'];

  const statusDescription = {
    'MUY ALTA': 'Más de 50 publicaciones activas',
    ALTA: 'Más de 11 publicaciones activas',
    MODERADA: 'Menos de 11 publicaciones activas',
    BAJA: 'Menos de 3 publicaciones activas',
    'MUY BAJA': 'Menos de 3 publicaciones activas',
  };

  // Filtrar publicaciones por portal
  const chileautosPublications = publications.list?.filter((pub) => pub.portal === 'Chileautos') || [];
  const facebookPublications = publications.list?.filter((pub) => pub.portal === 'Facebook') || [];
  const mercadoLibrePublications = publications.list?.filter((pub) => pub.portal === 'Mercado Libre') || [];
  const yapoPublications = publications.list?.filter((pub) => pub.portal === 'Yapo') || [];
  const otherPublications = publications.list?.filter((pub) => (
    pub.portal !== 'Chileautos'
    && pub.portal !== 'Facebook'
    && pub.portal !== 'Mercado Libre'
    && pub.portal !== 'Yapo'
  )) || [];

  // Función para obtener publicaciones según la pestaña activa
  function getPublicationsByTab(tab: string) {
    switch (tab) {
      case 'Chileautos':
        return chileautosPublications;
      case 'Facebook':
        return facebookPublications;
      case 'Mercado Libre':
        return mercadoLibrePublications;
      case 'Yapo':
        return yapoPublications;
      case 'Otras publicaciones':
        return otherPublications;
      default:
        return [];
    }
  }

  // Usar publicaciones filtradas para la tabla y paginación
  let filteredPublications = getPublicationsByTab(activeTab);
  filteredPublications = filteredPublications.slice().sort((a, b) => a.precio - b.precio);
  const totalPages = Math.ceil(filteredPublications.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredPublications.slice(indexOfFirstItem, indexOfLastItem);

  // Calcular el precio promedio publicado de Chileautos
  const averagePriceChileautos = chileautosPublications.reduce((acc, pub) => acc + pub.precio, 0) / chileautosPublications.length;

  // Calcular el tiempo promedio de publicación de Chileautos
  const averageDurationChileautos = Math.round(
    chileautosPublications.reduce((acc, pub) => {
      if (pub.fecha_est && createdAt) {
        try {
          const fechaEst = parseISO(pub.fecha_est);
          const created = parseISO(createdAt);
          return acc + Math.abs(differenceInDays(created, fechaEst));
        } catch {
          return acc;
        }
      }
      return acc;
    }, 0) / chileautosPublications.length,
  );

  // Calcular la velocidad promedio de venta de Chileautos
  let averageSpeedChileautos = '-';
  let textAverageSpeedChileautos = '';
  if (!Number.isNaN(averageDurationChileautos)) {
    if (averageDurationChileautos < 30) {
      averageSpeedChileautos = 'Rápida';
      textAverageSpeedChileautos = '(menos de 30 días)';
    } else if (averageDurationChileautos <= 60) {
      averageSpeedChileautos = 'Media';
      textAverageSpeedChileautos = '(30-60 días)';
    } else {
      averageSpeedChileautos = 'Lenta';
      textAverageSpeedChileautos = '(más de 60 días)';
    }
  }

  if (countPublications === 0) {
    return <CardWithoutInfo haveSimilarVersions={haveSimilarVersions} haveYearPrices={haveYearPrices} />;
  }

  return (
    <Container>
      <PaddingContainer>
        <SubCont9>
          <CardPrimaryData title="Precio promedio en publicaciones:" value={`$${priceFormat(roundPrice(averagePublications, 5))}`} text="" />
          <CardPrimaryData title="Publicaciones encontradas:" value={`${countPublications}`} text="" />
          <CardPrimaryData title="Presencia en publicaciones:" value={`${publications.status}`} text={statusDescription[publications.status as keyof typeof statusDescription] || ''} />
          <CardPrimaryData title="Duración promedio publicaciones" value={`${publications.average_duration ? `${Math.round(publications.average_duration)} días` : 'Sin información'}`} text="Promedio publicaciones activas y finalizadas últimos 60 días" />
        </SubCont9>
      </PaddingContainer>
      <PaddingContainer>
        <BlankSpace height="10px" />
        <Text4B>Listado de publicaciones encontradas</Text4B>
        <BlankSpace height="0px" />
        <Text3>Accede al listado de publicaciones encontradas por portal.</Text3>

        <TabContainer>
          {tabs.map((tab) => (
            <TabButton
              key={tab}
              active={activeTab === tab}
              onClick={() => {
                setActiveTab(tab);
                setCurrentPage(1); // Reinicia la página al cambiar de pestaña
              }}
            >
              {tab}
            </TabButton>
          ))}
        </TabContainer>

        <TableContainer>
          <Text4B>
            Publicaciones en
            {' '}
            {activeTab === 'Otras publicaciones' ? 'otros portales' : activeTab }
          </Text4B>
          <Table>
            <thead>
              <tr>
                <Th><Text4B>Precio</Text4B></Th>
                {activeTab !== 'Chileautos' && <Th><Text4B>Sitio o portal</Text4B></Th>}
                {activeTab === 'Chileautos' && (
                  <>
                    <Th><Text4B>Publicado hace</Text4B></Th>
                    <Th><Text4B>Tipo vendedor</Text4B></Th>
                  </>
                )}
                <Th><Text4B>Link</Text4B></Th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map((pub, idx) => {
                let dias = '-';
                if (pub.fecha_est && createdAt) {
                  try {
                    const fechaEst = parseISO(pub.fecha_est);
                    const created = parseISO(createdAt);
                    dias = `${String(Math.abs(differenceInDays(created, fechaEst)))} días`;
                  } catch {
                    dias = '-';
                  }
                }

                const url = `${b2bBaseUrl}/stock/valida_link?r=https://s3-sa-east-1.amazonaws.com/respaldo-fotos-extraccion/${pub.ss_path}&q=${pub.url_respaldo}`;
                const rowBg = idx % 2 === 0 ? '#fff' : '#F6F6F6';

                return (
                  <tr key={pub.url_respaldo} style={{ background: rowBg }}>
                    <Td>
                      <Text4B>
                        $
                        {priceFormat(pub.precio)}
                      </Text4B>
                    </Td>
                    {activeTab !== 'Chileautos' && (
                      <Td>
                        <Text4>
                          {pub.portal}
                        </Text4>
                      </Td>
                    )}
                    {activeTab === 'Chileautos' && (
                      <>
                        <Td>
                          <Text4>
                            {dias}
                          </Text4>
                        </Td>
                        <Td>
                          <Text4>
                            {pub.seller_type && pub.seller_type !== '0' ? capitalizeFirst(pub.seller_type) : 'No especificado'}
                          </Text4>
                        </Td>
                      </>
                    )}
                    <Td>
                      <LinkButton href={url} target="_blank" rel="noopener noreferrer">
                        Link publicación
                      </LinkButton>
                    </Td>
                  </tr>
                );
              })}
              {/* en caso de que no haya publicaciones, mostrar un mensaje */}
              {currentItems.length === 0 && (
                <tr>
                  <Td colSpan={4}>No hay publicaciones disponibles.</Td>
                </tr>
              )}
            </tbody>
          </Table>
        </TableContainer>

        <Pagination>
          <PaginationButton
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            &lt;
          </PaginationButton>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <PaginationButton
              key={page}
              active={currentPage === page}
              onClick={() => setCurrentPage(page)}
            >
              {page}
            </PaginationButton>
          ))}
          <PaginationButton
            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            &gt;
          </PaginationButton>
        </Pagination>
      </PaddingContainer>
      <PaddingContainer>
        <ChileautosContainer>
          <InfoChileautosAlliance>
            <img src={chileautosLogo} width={41} height={15} alt="" />
            <BlankSpace width="10px" />
            <Text3>Información exclusiva gracias a la alianza entre Autofact y Chileautos.</Text3>
          </InfoChileautosAlliance>
          <BlankSpace height="10px" />
          <SubCont9>
            <ChileautosCard title="Precio promedio publicado:" value={`${averagePriceChileautos ? `$${priceFormat(roundPrice(averagePriceChileautos, 5))}` : '-'}`} text="" />
            <ChileautosCard title="Promedio tiempo de publicación:" value={`${averageDurationChileautos ? `${averageDurationChileautos} días` : '-'}`} text="" />
            <ChileautosCard title="Velocidad promedio de venta:" value={averageSpeedChileautos} text={textAverageSpeedChileautos} />
            <ChileautosCard title="Ranking mas publicados:" value={`${rankingChileautos.ranking ? `${rankingChileautos.ranking}/100` : 'Este vehículo no está en el top 100 de los más publicados'}`} text="(En base al modelo con hasta 10 años de antigüedad)" />
          </SubCont9>
        </ChileautosContainer>
      </PaddingContainer>
    </Container>
  );
}

function CardPrimaryData({ title, value, text }: Readonly<{title: string, value: string, text: string | null}>) {
  return (
    <ImportantSettingsContainer>
      <ImportantSettingsSubCont1>
        <SubCont4>
          <TitlePrice>{title}</TitlePrice>
          <Text5B>
            {value}
          </Text5B>
        </SubCont4>
      </ImportantSettingsSubCont1>
      { text ? (
        <ImportantSettingsSubCont2>
          <AutoIconText
            text={text}
            text2=""
            icon={infoImg}
          />
        </ImportantSettingsSubCont2>
      ) : ''}
    </ImportantSettingsContainer>
  );
}

function AutoIconText({ text, text2, icon }: Readonly<{ text: string | null, text2: string | null, icon: string }>) {
  return (
    <AutoIconTextContainer>
      <img src={icon} width={15} height={15} alt="" />
      <BlankSpace width="15px" />
      <Text2>
        {text}
        <BlankSpace width="2px" />
        {text2}
      </Text2>
    </AutoIconTextContainer>
  );
}

function ChileautosCard({ title, value, text }: Readonly<{title: string, value: string, text: string | null}>) {
  return (
    <ChileautosCardContainer>
      <Text3>{title}</Text3>
      <BlankSpace height="5px" />
      <Text4B>{value}</Text4B>
      <BlankSpace height="5px" />
      <Text3>{text}</Text3>
    </ChileautosCardContainer>
  );
}

function CardWithoutInfo({ haveSimilarVersions, haveYearPrices }: Readonly<{ haveSimilarVersions: boolean, haveYearPrices: boolean }>) {
  return (
    <Container>
      <SubCont4 style={{ alignItems: 'center', textAlign: 'center', marginTop: '40px' }}>
        <Text5B style={{ color: '#0B3254', fontSize: '20px', marginBottom: '10px' }}>
          Lo sentimos, no hemos encontrado publicaciones de venta activas para este modelo.
        </Text5B>
        {haveSimilarVersions && haveYearPrices ? (
          <Text5 style={{ color: '#3D5176', fontSize: '18px', marginBottom: '30px' }}>
            Sin embargo puedes consultar en la siguiente sección precios para las versiones similares
            <br />
            o para la misma versión en diferentes años.
          </Text5>
        ) : ''}
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: '10px', marginTop: '20px', marginBottom: '20px', background: '#EBF7FF', borderRadius: '10px', padding: '10px', border: '2px dashed #3DB2FF',
        }}
        >
          <span role="img" aria-label="info" style={{ fontSize: '22px', marginTop: '2px' }}>
            <img src={bellImg} width={22} height={22} alt="" />
          </span>
          <div style={{ textAlign: 'left' }}>
            <Text4B style={{ color: '#0B3254' }}>¿Por qué no fue posible encontrar publicaciones activas?</Text4B>
            <BlankSpace height="2px" />
            <Text4 style={{ color: '#0B3254' }}>
              Esto puede deberse a que el modelo consultado tiene muy poca presencia de mercado ya sea por ser un modelo y versión muy antiguo o de un segmento con baja presencia como de alta gama.
            </Text4>
          </div>
        </div>
      </SubCont4>
    </Container>
  );
}

const ChileautosContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  width: 98%;
  border: 1px solid #C5C5C5;
  border-radius: 10px;
  padding: 10px;
  @media (max-width: 700px){
    width: auto;
  }
`;

const ChileautosCardContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  // justify-content: space-between;
  width: 100%;
`;

const InfoChileautosAlliance = styled.div`
  padding: 5px 5px;
  margin-top: 5px;
  border-radius: 5px;
  background: rgba(0, 124, 194, 0.15);
  width: 100%;
  display: flex;
  flex-direction: row;
`;

const AutoIconTextContainer = styled.div`
  width: 100%;
  margin: 10px 0px;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const Container = styled.div`
  padding: 10px 0;
  @media (max-width: 700px) {
    flex-direction: column;
  }
`;

const PaddingContainer = styled.div`
  padding: 0px 5px;
`;

const SubCont9 = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  width: 100%;
  @media (max-width: 700px){
    flex-direction: column;
    justify-content: center;
    gap: 20px;
  }
`;

const ImportantSettingsContainer = styled.div`
  margin: 10px;
  border: 1px solid #C5C5C5;
  background: #F6F6F6;
  border-radius: 10px;
  padding: 10px;
  width: 100%;
  @media (max-width: 700px){
    width: auto;
  }
`;

const ImportantSettingsSubCont1 = styled.div`
  flex-direction: row;
  margin-bottom: 0px;
  margin-top: 0px;
  justify-content: center;
  padding: 10px 0;
`;

const SubCont4 = styled.div`
  display: flex;
  flex-direction: column;
  @media (max-width: 700px){
    flex: 1;
  }
`;

const TitlePrice = styled.div`
  color: #0B3254;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
`;

const ImportantSettingsSubCont2 = styled.div`
  display: flex;
  flex-direction: column;
`;

const TabContainer = styled.div`
  display: flex;
  margin: 20px 0;
  border-bottom: 1px solid #E5E5E5;
  overflow-x: auto;
  @media (max-width: 700px) {
    flex-wrap: nowrap;
  }
`;

const TabButton = styled.button<{ active?: boolean }>`
  padding: 10px 20px;
  background: ${(props) => (props.active ? '#fff' : '#F6F6F6')};
  border: 1px solid #E5E5E5;
  border-bottom: ${(props) => (props.active ? 'none' : '1px solid #E5E5E5')};
  cursor: pointer;
  margin-bottom: -1px;
  white-space: nowrap;
  
  &:hover {
    background: #fff;
  }
`;

const TableContainer = styled.div`
  margin: 20px 0;
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  background: #F6F6F6;
  padding: 15px;
  text-align: left;
  color: #0B3254;
  font-weight: 700;
`;

const Td = styled.td`
  padding: 10px;
`;

const LinkButton = styled.a`
  color: #0B3254;
  text-decoration: underline;
  font-size: 14px;
  
  &:hover {
    text-decoration: none;
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  margin: 20px 0;
  gap: 5px;
`;

const PaginationButton = styled.button<{ active?: boolean, disabled?: boolean }>`
  padding: 5px 10px;
  border: 1px solid #E5E5E5;
  background: ${(props) => (props.active ? '#0066CC' : '#fff')};
  color: ${(props) => (props.active ? '#fff' : props.disabled ? '#C5C5C5' : '#000')};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  
  &:hover {
    // eslint-disable-next-line no-nested-ternary
    background: ${(props) => (props.active ? '#0066CC' : props.disabled ? '#fff' : '#F6F6F6')};
  }
`;

function capitalizeFirst(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}
